// InviteAccept.tsx — workspace-members cycle (THI-110 Task 5, .superpowers/sdd/
// 2026-08-20-workspace-members/). `/invite/:token`, mounted OUTSIDE `<Protected>` in App.tsx —
// an unauthenticated visitor is exactly who this page is FOR, unlike every other dashboard
// screen. Session-aware the way SignIn.tsx/SignUp.tsx are (reads `useSession` itself, no
// redirecting layout wraps it), not session-REQUIRED the way `Protected`/`SessionOnly` are.
//
// `GET /api/invites/:token` (public) resolves the card: a 404/network failure renders the ONE
// generic "invite is no longer valid" copy the API itself already unifies across every invalid
// reason (routes/invites.ts's `INVITE_INVALID_MESSAGE` — "no oracle about which failure", so
// this page doesn't invent a second, possibly-drifted copy of the same idea). A resolved view
// branches on session: signed out sees the invite's workspace/email and Sign in/Sign up links
// that carry the visitor back here afterward (lib/post-auth-redirect.ts); signed in auto-fires
// `POST /api/invites/:token/accept` and either lands on the new workspace or, on an email
// mismatch, is told whose invite this actually is with a way to sign out and try the right
// account.
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BrandLogo } from '@repo/ui/components/brand-logo';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { Skeleton } from '@repo/ui/components/skeleton';
import type { InviteAcceptResponseDto, InvitePublicViewDto } from '@repo/shared-types';
import { authClient, useSession } from '../lib/auth-client';
import { apiFetch, ApiError } from '../lib/api';
import { WORKSPACE_KEY } from '../lib/landing';
import { setPendingRedirect } from '../lib/post-auth-redirect';
import { useT } from '../locales/context';

/** Shared shell every card on this page renders inside — mirrors SignIn.tsx/SignUp.tsx's
 * centered, logo-topped card exactly, since this is reached by the same class of visitor
 * (unauthenticated, arriving from an email link) as those two. */
function InviteShell({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLogo size={40} />
        <Card className="w-full max-w-sm">{children}</Card>
      </div>
    </div>
  );
}

function InviteSkeleton() {
  return (
    <InviteShell>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-9 w-full rounded-md" />
      </CardContent>
    </InviteShell>
  );
}

/** Same generic copy the API itself already unifies across every invalid-token reason
 * (`routes/invites.ts`'s `INVITE_INVALID_MESSAGE`) — reused verbatim via `error.message` when
 * it's actually an `ApiError` so the two copies can never drift; the plain string is only a
 * fallback for a non-API failure (offline, etc.), where there IS no server message to reuse. */
function InvalidInviteCard({ message }: { message: string }) {
  const t = useT();
  return (
    <InviteShell>
      <CardHeader>
        <CardTitle>{t.auth.inviteNotValidTitle}</CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      <CardContent>
        <Link to="/" className="text-sm underline">
          {t.auth.goToEmbentLink}
        </Link>
      </CardContent>
    </InviteShell>
  );
}

export function InviteAccept() {
  const t = useT();
  const { token } = useParams<{ token: string }>();
  const { data: session, isPending: sessionPending } = useSession();

  const {
    data: view,
    isPending: viewPending,
    error: viewError,
  } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => apiFetch<InvitePublicViewDto>(`/api/invites/${token}`),
    enabled: Boolean(token),
    // A 404 here is a real, final answer ("this token is invalid") — not a transient failure
    // worth retrying, unlike React Query's default assumption.
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () =>
      apiFetch<InviteAcceptResponseDto>(`/api/invites/${token}/accept`, { method: 'POST' }),
    onSuccess: (data) => {
      // Hard navigation, not `navigate('/')` — same session/workspace-boundary rationale
      // SignIn.tsx documents on its own post-auth redirect: the newly-joined workspace becomes
      // active, and every already-mounted query (bots, billing, members, ...) was fetched
      // against whatever workspace the caller was in before accepting.
      localStorage.setItem(WORKSPACE_KEY, data.workspaceId);
      location.assign('/');
    },
  });

  // Auto-fires the accept POST the moment both are true: the token resolved to a real,
  // still-valid invite (`view` loaded) AND the visitor already has a session. A `useEffect`,
  // not a render-time `mutate()` call (contrast Bots.tsx's `?new=1` dialog-open, which adjusts
  // STATE during render per React's own guidance) — starting a network request is a genuine
  // side effect belonging in "synchronize with an external system", not the state-derivation
  // case that pattern is for.
  //
  // Fix round 1 (task-5 review, Medium) — guarded on a `useRef`, NOT `acceptMutation.status
  // === 'idle'` as originally written. `main.tsx` mounts the app under `<StrictMode>`, which
  // invokes an effect's first run twice in the same tick in dev; both invocations read the
  // SAME render's `acceptMutation` snapshot, so `status` reads `'idle'` both times and
  // `mutate()` — which has no re-entrancy guard of its own — fired twice, racing two concurrent
  // POST /accept calls (the loser landing on a 404 and flashing "invite no longer valid" right
  // after the winner had already succeeded). A ref sidesteps this because it mutates
  // SYNCHRONOUSLY and is shared across both StrictMode invocations, unlike state (which only
  // ever reflects the snapshot React handed this particular render). Writing to a ref here is
  // the sanctioned case of this repo's `react-hooks/refs` rule — that rule forbids reading OR
  // writing a ref during RENDER, not inside an effect (see Bots.tsx's own comment on the same
  // rule for the render-time case this ISN'T).
  const acceptFiredRef = useRef(false);
  useEffect(() => {
    if (view && session && !acceptFiredRef.current) {
      acceptFiredRef.current = true;
      acceptMutation.mutate();
    }
    // `acceptMutation` is a new object every render, so this effect body re-runs on every
    // render too — but `acceptFiredRef` still only lets `mutate()` actually fire once, so a
    // full-object dependency (exhaustive-deps' own preferred form here, over listing
    // `.mutate` alone) is exactly as safe as a narrower list would have been.
  }, [view, session, acceptMutation]);

  // Stash the return path the moment we know this visitor needs to authenticate first — read
  // back by SignIn.tsx (and best-effort by VerifyEmail.tsx) right before their own post-auth
  // redirect. See lib/post-auth-redirect.ts for why sessionStorage rather than a `?next=` those
  // pages read themselves.
  useEffect(() => {
    if (view && !session && token) {
      setPendingRedirect(`/invite/${token}`);
    }
  }, [view, session, token]);

  if (sessionPending || viewPending) return <InviteSkeleton />;

  if (viewError) {
    return (
      <InvalidInviteCard
        message={viewError instanceof ApiError ? viewError.message : t.auth.inviteInvalidFallback}
      />
    );
  }

  if (!view) return <InvalidInviteCard message={t.auth.inviteInvalidFallback} />;

  if (!session) {
    return (
      <InviteShell>
        <CardHeader>
          <CardTitle>{t.auth.inviteTitle({ workspaceName: view.workspaceName })}</CardTitle>
          <CardDescription>{t.auth.inviteDescription({ email: view.email })}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button asChild>
            <Link to={`/sign-in?next=/invite/${token}`}>{t.auth.inviteSignInCta}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/sign-up?next=/invite/${token}`}>{t.auth.inviteSignUpCta}</Link>
          </Button>
        </CardContent>
      </InviteShell>
    );
  }

  if (acceptMutation.error instanceof ApiError && acceptMutation.error.code === 'EMAIL_MISMATCH') {
    const mismatchMessage = acceptMutation.error.message;
    return (
      <InviteShell>
        <CardHeader>
          <CardTitle>{t.auth.wrongAccountTitle}</CardTitle>
          <CardDescription>{mismatchMessage}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => {
              // Same stash the "no session" branch's effect does — set directly here (not left
              // to that effect) because this button navigates AWAY from InviteAccept entirely
              // (`location.assign`, not a re-render into the "no session" branch), so that
              // effect would never get a chance to run for this particular round trip.
              if (token) setPendingRedirect(`/invite/${token}`);
              authClient.signOut({
                fetchOptions: { onSuccess: () => location.assign(`/sign-in?next=/invite/${token}`) },
              });
            }}
          >
            {t.auth.signOutCta}
          </Button>
        </CardContent>
      </InviteShell>
    );
  }

  if (acceptMutation.isError) {
    return (
      <InvalidInviteCard
        message={
          acceptMutation.error instanceof ApiError
            ? acceptMutation.error.message
            : t.auth.inviteAcceptFallback
        }
      />
    );
  }

  // Pending (the common case — the effect above fires almost immediately) or the brief instant
  // before it does.
  return (
    <InviteShell>
      <CardHeader>
        <CardTitle>{t.auth.joiningWorkspaceTitle({ workspaceName: view.workspaceName })}</CardTitle>
        <CardDescription>{t.auth.joiningWorkspaceBody}</CardDescription>
      </CardHeader>
    </InviteShell>
  );
}
